/**
 * Combined header-bell badge count: unread notifications + unseen helpable.
 *
 * Dexter's call: the app-wide bell must surface the helper-feed too ("we need
 * users to see it"), so the single badge becomes the SUM of two independently
 * polled counts rather than notifications alone.
 *
 * This is a pure read-only subscriber — it reads both caches via their value
 * hooks (`useNotificationCountValue` / `useHelperFeedCountValue`, both
 * `skipToken`, so they never fetch) and derives the sum. The polling lifecycle
 * for each count lives in BadgeProvider; this hook just re-renders when either
 * cache updates (e.g. a `helper_match.created` SSE bumps the helpable count).
 *
 * Per `vercel-react-best-practices` (`rerender-derived-state`): subscribe to the
 * derived value, not the raw objects — no useEffect, no mirrored state.
 */

import { useHelperFeedEnabled } from './useHelperFeedEnabled';
import { useNotificationCountValue } from './useNotificationCount';
import { useHelperFeedCountValue } from './useHelperFeedCount';

/**
 * @returns the total unseen count for the header bell: unread notifications
 * plus unseen helpable questions. Absent caches count as 0.
 *
 * The helpable term is gated on `useHelperFeedEnabled()`: while the helper feed
 * is off for this viewer there is no "You can help" tab to open, so the bell
 * surfaces ONLY the notification count (pre-helper-feed behavior). Admins
 * (`has_extra_options`) and a flipped flag turn the gate on, and the helpable
 * count rejoins the sum.
 */
export function useBellBadgeCount(): number {
  const helperFeedEnabled = useHelperFeedEnabled();
  const { data: notificationCount } = useNotificationCountValue();
  const { data: helperFeedCount } = useHelperFeedCountValue();

  const unreadNotifications = notificationCount?.unread_count ?? 0;
  const helperFeedCountValue = helperFeedCount?.unseen_count;
  const unseenHelpable = helperFeedEnabled ? (helperFeedCountValue ?? 0) : 0;

  return unreadNotifications + unseenHelpable;
}
